/**
 * Слэш-команды и кнопки в сообщениях для клиента (ADR 0010): список доступных команд и один
 * запрос «выполнить». Сервер сам отдаёт взаимодействие боту и ждёт его первого ответа до 3 секунд,
 * поэтому ответ на запрос сразу говорит, получилось ли.
 */
import { ApiError } from './api'
import { authed, describeError } from './auth'
import type { ApplicationCommandIndex, AttachmentRef, CommandChoice, InteractionDataOption, Snowflake } from '@/types/api'

export const commandsApi = {
  /** Команды на сервере: приложения его ботов, серверные команды и встроенная музыка. */
  index: (guildId: string) => authed<ApplicationCommandIndex>(`/guilds/${guildId}/application-command-index`),
  /** Команды в личке с ботом. */
  dmIndex: (channelId: string) => authed<ApplicationCommandIndex>(`/channels/${channelId}/application-command-index`),
}

/** data слэш-команды и автодополнения: значения опций, resolved собирает сервер. */
export interface CommandData {
  id: Snowflake
  name: string
  type: number
  options: InteractionDataOption[]
  /** Файлы опций-вложений: загружены тем же путём, что вложения сообщений; value опции — id отсюда. */
  attachments?: AttachmentRef[]
}

/** data нажатия кнопки или выбора в списке. */
export interface ComponentData {
  component_type: number
  custom_id: string
  values?: string[]
}

interface Target {
  /** У сообщения бота без взаимодействия приложения может быть не известно — его знает сервер по автору. */
  application_id?: Snowflake
  channel_id: Snowflake
  guild_id?: Snowflake
}

export type InteractionRequest =
  | (Target & { type: 2 | 4; application_id: Snowflake; data: CommandData })
  | (Target & { type: 3; message_id: Snowflake; data: ComponentData })

export type InteractionResult =
  | { kind: 'ok' }
  | { kind: 'choices'; choices: CommandChoice[] }
  | { kind: 'error'; status: number; message: string; retryAfter?: number | undefined }

/** Одноразовый nonce, как у сообщений: по нему сервер отличает повтор запроса. */
const nonce = () => `${Date.now()}${Math.floor(Math.random() * 1e6)}`

export const interactionsApi = {
  /**
   * POST /interactions. Ответ: 204 — бот ответил, 200 {choices} — автодополнение,
   * 504 — бот не ответил за 3 секунды, 400/403 — понятное сообщение сервера.
   * Не бросает: ошибка — тоже результат, его показывают у поля ввода или у кнопки.
   */
  run: async (body: InteractionRequest): Promise<InteractionResult> => {
    try {
      const res = await authed<{ choices?: CommandChoice[] } | undefined>('/interactions', {
        method: 'POST',
        body: JSON.stringify({ ...body, nonce: nonce() }),
      })
      if (res && Array.isArray(res.choices)) return { kind: 'choices', choices: res.choices }
      return { kind: 'ok' }
    } catch (err) {
      return { kind: 'error', ...interactionError(err) }
    }
  },
}

/** Текст ошибки взаимодействия. 504 и 10062 — бот не ответил вовремя. */
export function interactionError(err: unknown): { status: number; message: string; retryAfter?: number | undefined } {
  if (!(err instanceof ApiError)) return { status: 0, message: describeError(err).message }
  if (err.status === 504 || err.code === 10062) return { status: err.status, message: 'Приложение не ответило' }
  // предел — 5 взаимодействий за 5 секунд, и ждать там секунды, а не минуты, как у входа
  if (err.status === 429) return { status: 429, message: 'Слишком часто. Попробуйте через пару секунд', retryAfter: err.retryAfter }
  if (err.status >= 400 && err.status < 500 && err.message) return { status: err.status, message: err.message }
  return { status: err.status, message: 'Не получилось. Попробуйте ещё раз' }
}
